/** SA-1060 (spec 2026-10-09 §2.3): the closed line of every process editor
 * card, computed from the EFFECTIVE definition — the overlay over the base in
 * Customize, the draft with an empty overlay in Edit — so an edit shows on the
 * line without opening the card. Pure: no DOM, no store. */
import type { ProcessWorkflowDefinitionJson } from '@/types/workflowDefinition'
import type { WorkflowOverlayJson } from '@/types/workflowConfig'
import { durationText } from '@/utils/workflowOverview'
import { humanize } from '@/utils/friendlyLabels'

const SEP = ' · '

export const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`

/** English first, then any language, then the fallback (the viewer is an
 * admin surface; this is display resolution, not the SA-24 chain). */
export function labelOf(map: Record<string, string> | null | undefined, fallback: string): string {
  if (!map) return fallback
  return map.en ?? Object.values(map)[0] ?? fallback
}

type Overlay = WorkflowOverlayJson & {
  steps?: Record<string, Record<string, unknown>>
  seats?: Record<string, { label?: Record<string, string>; roles?: string[] }>
}

function readPath(node: unknown, path: string[]): unknown {
  let cursor: unknown = node
  for (const seg of path) {
    if (!cursor || typeof cursor !== 'object') return undefined
    cursor = (cursor as Record<string, unknown>)[seg]
  }
  return cursor
}

export function seatLabel(base: ProcessWorkflowDefinitionJson, overlay: WorkflowOverlayJson, seatId: string): string {
  const over = (overlay as Overlay).seats?.[seatId]?.label
  const own = base.seats?.[seatId]?.label
  return labelOf({ ...(own ?? {}), ...(over ?? {}) }, humanize(seatId))
}

export function stepSummary(base: ProcessWorkflowDefinitionJson, overlay: WorkflowOverlayJson, stepId: string): string {
  const step = base.steps?.[stepId]
  if (!step) return ''
  const patch = (overlay as Overlay).steps?.[stepId]
  const read = <T,>(path: string[], fallback: T): T => (readPath(patch, path) ?? fallback) as T
  const ex = step.executor
  const parts: string[] = []
  const dueAfter = read<string>(['due', 'after'], step.due?.after ?? '')
  const dueWords = dueAfter
    ? durationText({ after: dueAfter, calendar: read(['due', 'calendar'], step.due?.calendar ?? 'clock') })
    : null
  const hasSeat = ex.type === 'person' || ex.type === 'ai' || ex.type === 'wait'
  const seatId = hasSeat ? read<string>(['executor', 'seat'], ex.seat ?? '') : ''

  if (ex.type === 'wait') {
    parts.push('event' in ex.on ? `waits for ${humanize(ex.on.event).toLowerCase()}` : `waits ${durationText(ex.on)}`)
    if (seatId) parts.push(seatLabel(base, overlay, seatId))
  } else if (ex.type === 'person' || ex.type === 'ai') {
    if (seatId) parts.push(seatLabel(base, overlay, seatId))
    if (step.level) parts.push(read<string>(['level', 'default'], step.level.default))
  } else {
    parts.push('automatic')
  }
  if (dueWords) parts.push(`due ${dueWords}`)
  if (ex.type === 'person' || ex.type === 'ai') {
    const form = read<string[]>(['executor', 'form'], ex.form ?? step.produces ?? [])
    if (form.length > 0) parts.push(plural(form.length, 'form field'))
  }
  if (base.entry === stepId) parts.push('the entry step')
  // Never blank: a pasted step with nothing to say still names how it runs.
  if (parts.length === 0) parts.push(ex.type)
  return parts.join(SEP)
}

export function seatsSummary(base: ProcessWorkflowDefinitionJson, overlay: WorkflowOverlayJson): string {
  const seats = base.seats ?? {}
  const ids = Object.keys(seats)
  const unbound = ids.filter((id) => ((overlay as Overlay).seats?.[id]?.roles ?? seats[id].roles ?? []).length === 0)
  const tail = unbound.length === 0
    ? 'all bound'
    : `${unbound.length} unbound (${unbound.map((id) => seatLabel(base, overlay, id)).join(', ')})`
  return `${plural(ids.length, 'seat')}${SEP}${tail}`
}

export function fieldsSummary(base: ProcessWorkflowDefinitionJson): string {
  const fields = base.fields ?? {}
  const groups: Record<string, number> = {}
  for (const field of Object.values(fields)) {
    const group = (field as { restricted?: string | null }).restricted
    if (group) groups[group] = (groups[group] ?? 0) + 1
  }
  const parts = [plural(Object.keys(fields).length, 'field')]
  for (const [group, n] of Object.entries(groups)) parts.push(`${n} restricted to ${group}`)
  return parts.join(SEP)
}

export function lanesSummary(base: ProcessWorkflowDefinitionJson): string {
  const lanes = base.lanes ?? {}
  const parts = [plural(Object.keys(lanes).length, 'lane')]
  const names = Object.entries(lanes).map(([id, lane]) => labelOf(lane.label, humanize(id)))
  if (names.length > 0) parts.push(names.join(', '))
  return parts.join(SEP)
}

export function countLine(counts: {
  steps: number
  seats: number
  fields: number
  lanes?: number
  edited: number
  reordered?: boolean
}): string {
  const parts = [plural(counts.steps, 'step'), plural(counts.seats, 'seat'), plural(counts.fields, 'field')]
  if (counts.lanes !== undefined) parts.push(plural(counts.lanes, 'lane'))
  if (counts.edited > 0) parts.push(`${counts.edited} edited`)
  if (counts.reordered) parts.push('steps reordered')
  return parts.join(SEP)
}
